import React from 'react';
import { RefreshCw, TrendingUp, Flame, Loader2, Users, Heart } from 'lucide-react';
import { useCommunityWallet, useCirculationStats } from '../hooks/useCommunityWallet';

// ── Circulation Badge ─────────────────────────────────────────────
//
// Visual indicator shown on profiles that have a Community Tip Wallet.
// Shows the user's BCH tipping activity — how much they've received
// and re-tipped, computed entirely from Nostr tip receipt events.
// No blockchain API calls — 100% Nostr-native.

interface CirculationBadgeProps {
  pubkey: string;
  /** Show compact (inline) or expanded (card) version */
  variant?: 'inline' | 'card';
}

/** Get badge tier based on circulation score */
function getCirculationTier(score: number, txCount: number): {
  label: string;
  color: string;
  bgColor: string;
  icon: typeof RefreshCw;
  description: string;
} {
  if (txCount === 0) {
    return {
      label: 'New Circulator',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10 border-blue-500/20',
      icon: RefreshCw,
      description: 'Just set up their community wallet',
    };
  }
  if (score >= 80) {
    return {
      label: 'Super Circulator',
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10 border-orange-500/20',
      icon: Flame,
      description: 'Re-tips most of what they receive',
    };
  }
  if (score >= 50) {
    return {
      label: 'Active Circulator',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10 border-emerald-500/20',
      icon: TrendingUp,
      description: 'Actively circulating BCH in the community',
    };
  }
  if (score >= 20) {
    return {
      label: 'Circulator',
      color: 'text-primary',
      bgColor: 'bg-primary/10 border-primary/20',
      icon: RefreshCw,
      description: 'Participating in the community economy',
    };
  }
  return {
    label: 'Circulator',
    color: 'text-muted-foreground',
    bgColor: 'bg-muted border-border',
    icon: RefreshCw,
    description: 'Community wallet active',
  };
}

function formatSats(sats: number): string {
  if (sats >= 100_000_000) return (sats / 100_000_000).toFixed(2) + ' BCH';
  if (sats >= 1_000_000) return (sats / 1_000_000).toFixed(1) + 'M sats';
  if (sats >= 1_000) return (sats / 1_000).toFixed(1) + 'k sats';
  return sats.toLocaleString() + ' sats';
}

/** Inline badge — shown next to username or in profile header */
export function CirculationBadgeInline({ pubkey }: { pubkey: string }) {
  const { address, split } = useCommunityWallet(pubkey);

  if (!address) return null;

  const label = split === 100
    ? 'Circulating'
    : split > 0
      ? `${split}% Circulating`
      : 'Circulating';

  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-[10px] font-medium"
      title={`Community Tip Wallet — ${split}% of tips stay circulating`}
    >
      <RefreshCw className="w-2.5 h-2.5" />
      <span>{label}</span>
    </span>
  );
}

/** Full card — shown on profile pages with stats */
export default function CirculationBadge({ pubkey, variant = 'card' }: CirculationBadgeProps) {
  const { address, split, loading: walletLoading } = useCommunityWallet(pubkey);
  const stats = useCirculationStats(pubkey);

  // Don't show anything if user has no community wallet
  if (!walletLoading && !address) return null;
  if (walletLoading) return null;

  if (variant === 'inline') {
    return <CirculationBadgeInline pubkey={pubkey} />;
  }

  const tier = getCirculationTier(stats.circulationScore, stats.txCount);
  const Icon = tier.icon;

  return (
    <div className={`rounded-xl border p-3 ${tier.bgColor}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${tier.bgColor}`}>
            <Icon className={`w-4 h-4 ${tier.color}`} />
          </div>
          <div>
            <p className={`text-xs font-semibold ${tier.color}`}>{tier.label}</p>
            <p className="text-[10px] text-muted-foreground">{tier.description}</p>
          </div>
        </div>

        {/* Circulation Score */}
        {!stats.loading && stats.txCount > 0 && (
          <div className="text-right">
            <p className={`text-lg font-bold ${tier.color}`}>{stats.circulationScore}%</p>
            <p className="text-[9px] text-muted-foreground">circulation</p>
          </div>
        )}
      </div>

      {/* Stats */}
      {stats.loading ? (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading stats...
        </div>
      ) : stats.txCount > 0 ? (
        <div className="space-y-2.5 mt-3">
          {/* Sats flow */}
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center">
              <p className="text-xs font-semibold text-foreground">{formatSats(stats.totalReceived)}</p>
              <p className="text-[9px] text-muted-foreground">Received</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-foreground">{formatSats(stats.totalSent)}</p>
              <p className="text-[9px] text-muted-foreground">Re-tipped</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-foreground">{stats.txCount}</p>
              <p className="text-[9px] text-muted-foreground">Tips</p>
            </div>
          </div>
          {/* Social stats */}
          <div className="flex items-center justify-center gap-4 text-[10px] text-muted-foreground">
            {stats.uniqueSupporters > 0 && (
              <span className="flex items-center gap-1">
                <Heart className="w-3 h-3 text-pink-500/60" />
                {stats.uniqueSupporters} {stats.uniqueSupporters === 1 ? 'supporter' : 'supporters'}
              </span>
            )}
            {stats.uniqueSupported > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3 text-primary/60" />
                {stats.uniqueSupported} supported
              </span>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[10px] text-muted-foreground mt-2">
          Community wallet is set up and ready to receive tips. Start tipping to build your circulation score!
        </p>
      )}

      {/* Split info + wallet address */}
      {address && (
        <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
          {split < 100 && split > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-emerald-500/60 rounded-full"
                  style={{ width: `${split}%` }}
                />
              </div>
              <span className="text-[9px] text-muted-foreground whitespace-nowrap">
                {split}% circulating · {100 - split}% personal
              </span>
            </div>
          )}
          <p className="text-[9px] text-muted-foreground font-mono truncate">
            {address.replace(/^bitcoincash:/i, '')}
          </p>
        </div>
      )}
    </div>
  );
}

